import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { userService, getErrorMessage } from '@/services';
import type { UserFilters, UserUpdateRequest } from '@/types';
import { INSURER_ASSOCIATION_MISSING_HINT, type AdminUserCreatePayload } from '@/services/users';

export function useUsers(page: number = 1, filters?: UserFilters) {
  return useQuery({
    queryKey: ['users', page, filters],
    queryFn: () => userService.getUsers(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUser(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdminUserCreatePayload) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
      toast.success('User updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.activateUser(id),
    onSuccess: (user, id) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast.success('User activated successfully');
      if (user?.insurer_association_missing) {
        toast(INSURER_ASSOCIATION_MISSING_HINT, { duration: 10000 });
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      toast.success('User deactivated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Issue a temporary password for a user.
 *
 * This used to toast "Password reset email sent". No email was sent and none
 * could be -- no delivery channel is configured on this platform (see
 * apps/users/CLAUDE.md; EMAIL_HOST is still smtp.example.com) -- and the
 * endpoint behind it did not exist at all, so the toast fired on a 404-shaped
 * promise having done precisely nothing. The admin walked away believing the
 * user had been emailed.
 *
 * The mutation now RESOLVES WITH the server's payload so the caller can show
 * the password (it is not recoverable afterwards), and the toast says only what
 * actually happened.
 */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: string) => userService.resetPassword(id),
    onSuccess: () => {
      toast.success('Temporary password issued - copy it before closing.');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
